import React, { useEffect, useState } from "react";
import {
  Star,
  User,
  CheckCircle,
  XCircle,
  Check,
  X,
  Loader,
  Edit,
  X as CloseIcon,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import adminService from "@/services/api/admin.service";

const ExpertList = () => {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    specialties: [],
    degrees: [],
    certifications: [],
    phone: "",
    website: "",
    availableStartTime: "",
    availableEndTime: "",
    breakStartTime: "",
    breakEndTime: "",
    timezone: "",
    status: "",
    email: "",
    profileImage: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchingExpertDetails, setFetchingExpertDetails] = useState(false);

  // Get auth token from local storage or your auth context
  const getAuthToken = () => {
    // Replace with your actual token retrieval logic
    return localStorage.getItem("authToken");
  };

  useEffect(() => {
    const fetchExperts = async () => {
      try {
        const data = await adminService.listExperts({ page: currentPage });
        setExperts(data.experts);
        setTotalPages(data.totalPages);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        toast.error(`Failed to fetch experts: ${err.message}`);
      }
    };

    fetchExperts();
  }, [currentPage]);

  const handleVerifyExpert = async (expert) => {
    try {
      await adminService.verifyExpert(expert.id);
      toast.success("Expert verified successfully");

      const updatedExperts = experts.map((e) => {
        if (e.id === expert.id) {
          return { ...e, isVerified: true };
        }
        return e;
      });
      setExperts(updatedExperts);
    } catch (err) {
      toast.error(err.message || "Failed to verify expert");
    }
  };

  const handleSuspendExpert = async (expertId) => {
    try {
      await adminService.suspendExpert(expertId);
      toast.success("Expert suspended successfully");

      setExperts((prev) =>
        prev.map((expert) =>
          expert.id === expertId ? { ...expert, status: "suspended" } : expert
        )
      );
    } catch (err) {
      toast.error(err.message || "Failed to suspend expert");
    }
  };

  const handleEditExpert = async (expert) => {
    setSelectedExpert(expert);
    setFetchingExpertDetails(true);

    try {
      const token = getAuthToken();

      // ✅ Use the adminService method instead of fetch
      const data = await adminService.fetchExpertDetails(expert.id, token);

      const expertInfo = data.expertInfo || data.expert || data;
      const availability = data.availability || {};

      setFormData({
        firstName: expertInfo.firstName || "",
        lastName: expertInfo.lastName || "",
        email: expertInfo.email || "",
        specialties: expertInfo.specialties || [],
        degrees: expertInfo.credentials?.degrees || expertInfo.degrees || [],
        certifications:
          expertInfo.credentials?.certifications ||
          expertInfo.certifications ||
          [],
        phone: expertInfo.contactInfo?.phone || expertInfo.phone || "",
        website:
          expertInfo.contactInfo?.professionalWebsite ||
          expertInfo.website ||
          "",
        availableStartTime:
          expertInfo.availableStartTime ||
          availability.availableStartTime ||
          "",
        availableEndTime:
          expertInfo.availableEndTime || availability.availableEndTime || "",
        breakStartTime:
          expertInfo.breakStartTime || availability.breakStartTime || "",
        breakEndTime:
          expertInfo.breakEndTime || availability.breakEndTime || "",
        timezone: expertInfo.timezone || availability.timezone || "",
        status: expertInfo.status || "active",
        profileImage: expertInfo.profileImage || "",
      });

      setIsEditModalOpen(true);
    } catch (err) {
      alert("Failed to fetch expert details: " + err.message);
    } finally {
      setFetchingExpertDetails(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSpecialtiesChange = (e) => {
    const value = e.target.value;
    const specialtiesArray = value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item);
    setFormData((prev) => ({ ...prev, specialties: specialtiesArray }));
  };

  const handleCredentialsChange = (type, e) => {
    const value = e.target.value;
    const namesList = value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item);

    // If the existing credentials are complex objects with additional fields
    if (
      formData[type] &&
      formData[type].length > 0 &&
      typeof formData[type][0] === "object"
    ) {
      // Create a map of existing credentials with name as key for quick lookup
      const existingCredMap = {};
      formData[type].forEach((cred) => {
        if (cred.name) {
          existingCredMap[cred.name] = cred;
        }
      });

      // For each name in the input, either keep existing data or create new entry
      const updatedCreds = namesList.map((name) => {
        // If this name already exists in original data, preserve its additional fields
        if (existingCredMap[name]) {
          return existingCredMap[name];
        }

        // Otherwise create a new credential object with defaults based on type
        if (type === "degrees") {
          return {
            name: name,
            institution: "",
            year: new Date().getFullYear(),
          };
        } else if (type === "certifications") {
          return {
            name: name,
            issuingOrganization: "",
            issueDate: new Date().toISOString().split("T")[0],
          };
        }
        return { name };
      });

      setFormData((prev) => ({ ...prev, [type]: updatedCreds }));
    } else {
      // Handle simple string array case
      setFormData((prev) => ({ ...prev, [type]: namesList }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";

    // Validate time slots if all are provided
    if (
      formData.availableStartTime &&
      formData.availableEndTime &&
      formData.breakStartTime &&
      formData.breakEndTime
    ) {
      // Convert times to minutes for comparison
      const timeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(":").map(Number);
        return hours * 60 + minutes;
      };

      const availStartMins = timeToMinutes(formData.availableStartTime);
      const availEndMins = timeToMinutes(formData.availableEndTime);
      const breakStartMins = timeToMinutes(formData.breakStartTime);
      const breakEndMins = timeToMinutes(formData.breakEndTime);

      if (breakStartMins <= availStartMins) {
        errors.breakStartTime =
          "Break start time must be after availability start time";
      }

      if (breakEndMins >= availEndMins) {
        errors.breakEndTime =
          "Break end time must be before availability end time";
      }

      if (breakStartMins >= breakEndMins) {
        errors.breakTime = "Break start time must be before break end time";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const token = getAuthToken();

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        specialties: formData.specialties,
        credentials: {
          degrees: formData.degrees,
          certifications: formData.certifications,
        },
        contactInfo: {
          phone: formData.phone,
          professionalWebsite: formData.website,
        },
        status: formData.status,
      };

      if (formData.availableStartTime)
        payload.availableStartTime = formData.availableStartTime;
      if (formData.availableEndTime)
        payload.availableEndTime = formData.availableEndTime;
      if (formData.breakStartTime)
        payload.breakStartTime = formData.breakStartTime;
      if (formData.breakEndTime) payload.breakEndTime = formData.breakEndTime;
      if (formData.timezone) payload.timezone = formData.timezone;

      // ✅ Use adminService instead of fetch
      await adminService.updateExpertDetails(selectedExpert.id, payload, token);

      setExperts((prevExperts) =>
        prevExperts.map((expert) => {
          if (expert.id === selectedExpert.id) {
            return {
              ...expert,
              fullName: `${formData.firstName} ${formData.lastName}`,
              specialties: formData.specialties,
              email: formData.email,
              status: formData.status,
              availableStartTime: formData.availableStartTime,
              availableEndTime: formData.availableEndTime,
            };
          }
          return expert;
        })
      );

      toast.success("Expert updated successfully");
      setIsEditModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to update expert");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to format credential arrays for display
  const formatCredentialsForDisplay = (credentials) => {
    if (!credentials || credentials.length === 0) return "";

    if (typeof credentials[0] === "string") {
      return credentials.join(", ");
    } else if (typeof credentials[0] === "object") {
      // For complex credential objects, just return the name part for the input display
      return credentials.map((cred) => cred.name).join(", ");
    }

    return "";
  };

  return (
    <div className="p-6 rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Expert Management</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
        </div>
      ) : error ? (
        <div className="p-4 rounded-lg max-w-md mx-auto text-center">
          <h3 className="text-lg font-medium text-red-800">Error</h3>
          <p className="text-red-600 mt-2">{error}</p>
        </div>
      ) : experts.length === 0 ? (
        <div className="p-8 rounded-lg shadow text-center">
          <p className="text-gray-600">
            No experts found matching your criteria.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 shadow-md rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                  Name
                </th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                  Email
                </th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                  Status
                </th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                  Specialties
                </th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                  Rating
                </th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                  Available Time
                </th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {experts.map((expert) => {
                const averageRating = expert.rating?.average || 0;
                const totalReviews = expert.rating?.totalReviews || 0;

                return (
                  <tr key={expert.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 mr-3">
                          {expert.profileImage ? (
                            <img
                              src={expert.profileImage}
                              alt={expert.fullName || ""}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <User size={18} className="text-gray-500" />
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-800 flex items-center">
                            {expert.fullName || "Expert"}
                            {expert.isVerified ? (
                              <CheckCircle
                                size={16}
                                className="text-green-600 ml-2"
                                title="Verified"
                              />
                            ) : (
                              <XCircle
                                size={16}
                                className="text-red-600 ml-2"
                                title="Not Verified"
                              />
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {expert.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {expert.email || ""}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          expert.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {expert.status === "active" ? "Active" : "Suspended"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {expert.specialties?.length > 0 ? (
                          expert.specialties
                            .slice(0, 2)
                            .map((specialty, index) => (
                              <span
                                key={index}
                                className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full"
                              >
                                {specialty}
                              </span>
                            ))
                        ) : (
                          <span className="text-gray-500 text-xs">None</span>
                        )}
                        {expert.specialties?.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{expert.specialties.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <Star
                          size={14}
                          className="text-yellow-500 mr-1"
                          fill="currentColor"
                        />
                        <span className="text-sm">
                          {averageRating.toFixed(1)}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">
                          ({totalReviews})
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {expert.availableStartTime && expert.availableEndTime ? (
                        <span className="text-green-600">
                          {expert.availableStartTime} -{" "}
                          {expert.availableEndTime}
                        </span>
                      ) : (
                        <span className="text-gray-500">Not set</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        {!expert.isVerified && (
                          <button
                            onClick={() => handleVerifyExpert(expert)}
                            className="p-1 rounded bg-green-100 text-green-600 hover:bg-green-200"
                            title="Verify"
                          >
                            <Check size={16} />
                          </button>
                        )}
                        {expert.status !== "suspended" && (
                          <button
                            onClick={() => handleSuspendExpert(expert.id)}
                            className="p-1 rounded bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                            title="Suspend"
                          >
                            <X size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => handleEditExpert(expert)}
                          className="p-1 rounded bg-blue-100 text-blue-600 hover:bg-blue-200"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 mx-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
          >
            Previous
          </button>
          {[...Array(totalPages).keys()].map((_, index) => (
            <button
              key={index + 1}
              onClick={() => setCurrentPage(index + 1)}
              className={`px-4 py-2 mx-1 rounded-md ${
                currentPage === index + 1
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {index + 1}
            </button>
          ))}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 mx-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Expert Modal */}
      {isEditModalOpen && selectedExpert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-3 border-b">
              <h2 className="text-lg font-medium text-gray-800">
                Edit: {formData.firstName} {formData.lastName}
              </h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <CloseIcon size={16} />
              </button>
            </div>

            {fetchingExpertDetails ? (
              <div className="flex items-center justify-center p-6">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4">
                {/* Tabs for different sections */}
                <div className="mb-4 border-b">
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => setActiveTab("basic")}
                      className={`py-2 px-3 text-sm font-medium ${
                        activeTab === "basic"
                          ? "text-blue-600 border-b-2 border-blue-600"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Basic Info
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("credentials")}
                      className={`py-2 px-3 text-sm font-medium ${
                        activeTab === "credentials"
                          ? "text-blue-600 border-b-2 border-blue-600"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Credentials
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("availability")}
                      className={`py-2 px-3 text-sm font-medium ${
                        activeTab === "availability"
                          ? "text-blue-600 border-b-2 border-blue-600"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Availability
                    </button>
                  </div>
                </div>

                {/* Basic Information Tab */}
                {activeTab === "basic" && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className={`w-full p-1.5 text-sm border rounded ${
                            formErrors.firstName
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {formErrors.firstName && (
                          <p className="text-red-500 text-xs mt-0.5">
                            {formErrors.firstName}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className={`w-full p-1.5 text-sm border rounded ${
                            formErrors.lastName
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {formErrors.lastName && (
                          <p className="text-red-500 text-xs mt-0.5">
                            {formErrors.lastName}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="text"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full p-1.5 text-sm border rounded ${
                          formErrors.email
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {formErrors.email && (
                        <p className="text-red-500 text-xs mt-0.5">
                          {formErrors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Specialties
                      </label>
                      <input
                        type="text"
                        name="specialties"
                        value={
                          Array.isArray(formData.specialties)
                            ? formData.specialties.join(", ")
                            : ""
                        }
                        onChange={handleSpecialtiesChange}
                        placeholder="Comma separated values"
                        className="w-full p-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="w-full p-1.5 text-sm border border-gray-300 rounded"
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Credentials Tab */}
                {activeTab === "credentials" && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Degrees
                      </label>
                      <input
                        type="text"
                        name="degrees"
                        value={formatCredentialsForDisplay(formData.degrees)}
                        onChange={(e) => handleCredentialsChange("degrees", e)}
                        placeholder="Comma separated values"
                        className="w-full p-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Certifications
                      </label>
                      <input
                        type="text"
                        name="certifications"
                        value={formatCredentialsForDisplay(
                          formData.certifications
                        )}
                        onChange={(e) =>
                          handleCredentialsChange("certifications", e)
                        }
                        placeholder="Comma separated values"
                        className="w-full p-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full p-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Professional Website
                      </label>
                      <input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        className="w-full p-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                )}

                {/* Availability Tab */}
                {activeTab === "availability" && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Available From
                        </label>
                        <input
                          type="time"
                          name="availableStartTime"
                          value={formData.availableStartTime}
                          onChange={handleInputChange}
                          className="w-full p-1.5 text-sm border border-gray-300 rounded"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Available To
                        </label>
                        <input
                          type="time"
                          name="availableEndTime"
                          value={formData.availableEndTime}
                          onChange={handleInputChange}
                          className="w-full p-1.5 text-sm border border-gray-300 rounded"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Break Start
                        </label>
                        <input
                          type="time"
                          name="breakStartTime"
                          value={formData.breakStartTime}
                          onChange={handleInputChange}
                          className={`w-full p-1.5 text-sm border rounded ${
                            formErrors.breakStartTime || formErrors.breakTime
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {formErrors.breakStartTime && (
                          <p className="text-red-500 text-xs mt-0.5">
                            {formErrors.breakStartTime}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Break End
                        </label>
                        <input
                          type="time"
                          name="breakEndTime"
                          value={formData.breakEndTime}
                          onChange={handleInputChange}
                          className={`w-full p-1.5 text-sm border rounded ${
                            formErrors.breakEndTime || formErrors.breakTime
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {formErrors.breakEndTime && (
                          <p className="text-red-500 text-xs mt-0.5">
                            {formErrors.breakEndTime}
                          </p>
                        )}
                      </div>
                    </div>

                    {formErrors.breakTime && (
                      <p className="text-red-500 text-xs">
                        {formErrors.breakTime}
                      </p>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Timezone
                      </label>
                      <input
                        type="text"
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleInputChange}
                        placeholder="e.g. UTC, America/New_York"
                        className="w-full p-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                )}

                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertList;
